import _extends from "@babel/runtime/helpers/esm/extends";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _dec, _class, _class2, _temp;

import React from 'react';
import { func, number } from 'prop-types';
import { globalObject } from '@skbkontur/global-object';
import { isInstanceOf } from "../../../lib/isInstanceOf";
import { isKeyArrowLeft, isKeyArrowRight, isKeyEnter } from "../../../lib/events/keyboard/identifiers";
import { locale } from "../../../lib/locale/decorators";
import { keyListener } from "../../../lib/events/keyListener";
import { emptyHandler } from "../../../lib/utils";
import { isIE11 } from "../../../lib/client";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { ArrowChevronRightIcon } from "../../../internal/icons/16px";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { cx } from "../../../lib/theming/Emotion";
import { rootNode } from "../../../lib/rootNode";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { getVisualStateDataAttributes } from "../../../internal/CommonWrapper/utils/getVisualStateDataAttributes";
import { styles } from "../Paging.styles";
import * as NavigationHelper from "../NavigationHelper";
import { getItems } from "../PagingHelper";
import { PagingLocaleHelper } from "../locale";
import { PagingDefaultComponent } from "../PagingDefaultComponent";
import { ForwardIcon } from "../ForwardIcon";
var IGNORE_EVENT_TAGS = ['input', 'textarea'];
export var PagingDataTids = {
  root: 'Paging__root',
  dots: 'Paging__dots',
  forwardLink: 'Paging__forwardLink',
  pageLinkWrapper: 'Paging__pageLinkWrapper',
  pageLink: 'Paging__pageLink'
};
export var Paging = (_dec = locale('Paging', PagingLocaleHelper), rootNode(_class = _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(Paging, _React$PureComponent);

  function Paging() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Paging.defaultProps);
    _this.state = {
      focusedByTab: false,
      focusedItem: null,
      keyboardControl: _this.getProps().useGlobalListener
    };
    _this.addedGlobalListener = false;
    _this.container = null;

    _this.renderItem = function (item, index) {
      var focused = _this.getFocusedItem() === item;

      switch (item) {
        case '.':
          {
            var key = "dots" + (index < 5 ? 'Left' : 'Right');
            return _this.renderDots(key);
          }

        case 'forward':
          {
            var disabled = _this.isItemDisabled(item);

            return _this.renderForwardLink(disabled, focused);
          }

        default:
          {
            var active = _this.props.activePage === item;
            return _this.renderPageLink(item, active, focused);
          }
      }
    };

    _this.renderDots = function (key) {
      var _cx;

      return /*#__PURE__*/React.createElement("span", {
        "data-tid": PagingDataTids.dots,
        key: key,
        className: cx((_cx = {}, _cx[styles.dots(_this.theme)] = true, _cx[styles.dotsDisabled(_this.theme)] = _this.props.disabled, _cx))
      }, '...');
    };

    _this.renderForwardLink = function (disabled, focused) {
      var _cx2;

      var classes = isTheme2022(_this.theme) ? cx(styles.pageLink(_this.theme), styles.forwardLink(_this.theme), focused && styles.pageLinkFocused(_this.theme), (disabled || _this.props.disabled) && styles.pageLinkDisabled(_this.theme)) : cx((_cx2 = {}, _cx2[styles.forwardLink(_this.theme)] = true, _cx2[styles.forwardLinkFocused()] = focused, _cx2[styles.forwardLinkDisabled(_this.theme)] = disabled || _this.props.disabled, _cx2));

      var Component = _this.getProps().component;

      var forward = _this.locale.forward;
      var forwardIcon = isTheme2022(_this.theme) ? /*#__PURE__*/React.createElement(ForwardIcon, {
        size: parseInt(_this.theme.pagingForwardIconSize),
        style: {
          marginLeft: 4
        }
      }) : /*#__PURE__*/React.createElement("span", {
        className: styles.forwardIcon(_this.theme)
      }, /*#__PURE__*/React.createElement(ArrowChevronRightIcon, {
        size: _this.theme.pagingForwardIconSize
      }));
      return /*#__PURE__*/React.createElement(Component, _extends({
        key: 'forward',
        "data-tid": PagingDataTids.forwardLink,
        active: false,
        className: classes,
        onClick: disabled ? emptyHandler : _this.goForward,
        tabIndex: -1,
        pageNumber: 'forward'
      }, getVisualStateDataAttributes({
        disabled: disabled
      })), _this.props.caption || forward, forwardIcon);
    };

    _this.renderPageLink = function (pageNumber, active, focused) {
      var _cx3;

      var disabled = _this.props.disabled;
      var classes = cx((_cx3 = {}, _cx3[styles.pageLink(_this.theme)] = true, _cx3[styles.pageLinkFocused(_this.theme)] = focused, _cx3[styles.pageLinkDisabled(_this.theme)] = disabled, _cx3[styles.pageLinkCurrent(_this.theme)] = active, _cx3[styles.pageLinkCurrentDisabled(_this.theme)] = active && disabled, _cx3));

      var Component = _this.getProps().component;

      var handleClick = function handleClick() {
        return _this.goToPage(pageNumber);
      };

      return /*#__PURE__*/React.createElement("span", _extends({
        "data-tid": PagingDataTids.pageLinkWrapper,
        key: pageNumber,
        className: styles.pageLinkWrapper()
      }, getVisualStateDataAttributes({
        active: active,
        disabled: disabled
      }), {
        onMouseDown: _this.handleMouseDownPageLink
      }), /*#__PURE__*/React.createElement(Component, {
        "data-tid": PagingDataTids.pageLink,
        active: active,
        className: classes,
        onClick: handleClick,
        tabIndex: -1,
        pageNumber: pageNumber
      }, pageNumber), active && _this.renderNavigationHint());
    };

    _this.renderNavigationHint = function () {
      if (_this.props.withoutNavigationHint) {
        return null;
      }

      var keyboardControl = _this.state.keyboardControl;

      var canGoBackward = _this.canGoBackward();

      var canGoForward = _this.canGoForward();

      var hint = null;

      if (keyboardControl && (canGoBackward || canGoForward)) {
        hint = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
          className: canGoBackward ? '' : styles.transparent()
        }, '←'), /*#__PURE__*/React.createElement("span", null, NavigationHelper.getKeyName()), /*#__PURE__*/React.createElement("span", {
          className: canGoForward ? '' : styles.transparent()
        }, '→'));
      }

      return /*#__PURE__*/React.createElement("div", {
        className: styles.pageLinkHint(_this.theme)
      }, hint);
    };

    _this.handleMouseDown = function () {
      _this.setState({
        focusedByTab: false,
        focusedItem: null
      });
    };

    _this.handleMouseDownPageLink = function () {
      if (isIE11) {
        // Клик по span внутри контейнера с tabindex="0" переносит фокус именно на этот span.
        // Поэтому горячие клавиши работают пока span существует на странице.
        globalObject.setTimeout(function () {
          return _this.container && _this.container.focus();
        }, 0);
      }
    };

    _this.handleKeyDown = function (e) {
      if (e.shiftKey) {
        return;
      }

      var target = e.target;
      var isArrowLeft = isKeyArrowLeft(e);
      var isArrowRight = isKeyArrowRight(e);

      if (isInstanceOf(target, globalObject.Element) && (IGNORE_EVENT_TAGS.includes(target.tagName.toLowerCase()) || target.isContentEditable)) {
        return;
      }

      if (NavigationHelper.checkKeyPressed(e) && isArrowLeft) {
        _this.setState({
          focusedItem: null
        }, _this.goBackward);

        return;
      }

      if (NavigationHelper.checkKeyPressed(e) && isArrowRight) {
        _this.setState({
          focusedItem: null
        }, _this.goForward);

        return;
      }

      if (_this.container && _this.container === e.target) {
        if (isArrowLeft) {
          _this.setState({
            focusedByTab: true
          }, _this.moveFocusLeft);

          return;
        }

        if (isArrowRight) {
          _this.setState({
            focusedByTab: true
          }, _this.moveFocusRight);

          return;
        }

        if (isKeyEnter(e)) {
          _this.executeItemAction(_this.getFocusedItem());

          return;
        }
      }
    };

    _this.handleFocus = function () {
      if (_this.props.disabled) {
        return;
      }

      _this.setState({
        keyboardControl: true
      }); // focus event fires before keyDown eventlistener
      // so we should check tabPressed in async way


      globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
        if (keyListener.isTabPressed) {
          _this.setState({
            focusedByTab: true
          });
        }
      });
    };

    _this.handleBlur = function () {
      _this.setState({
        focusedByTab: false,
        keyboardControl: _this.getProps().useGlobalListener || false
      });
    };

    _this.getItems = function () {
      return getItems(_this.props.activePage, _this.props.pagesCount).concat('forward');
    };

    _this.getFocusedItem = function () {
      if (!_this.state.focusedByTab) {
        return null;
      }

      var focusedItem = _this.state.focusedItem;

      if (focusedItem && _this.getItems().includes(focusedItem) && _this.isItemFocusable(focusedItem)) {
        return focusedItem;
      }

      return _this.props.activePage;
    };

    _this.isItemFocusable = function (item) {
      return !_this.isItemDisabled(item);
    };

    _this.isItemDisabled = function (item) {
      switch (item) {
        case '.':
          return true;

        case 'forward':
          return !_this.canGoForward();

        default:
          return false;
      }
    };

    _this.executeItemAction = function (item) {
      if (item === 'forward') {
        _this.goForward();
      }

      if (typeof item === 'number') {
        _this.goToPage(item);
      }
    };

    _this.moveFocusLeft = function () {
      _this.moveFocus(-1);
    };

    _this.moveFocusRight = function () {
      _this.moveFocus(1);
    };

    _this.moveFocus = function (step) {
      var focusedItem = _this.getFocusedItem();

      var items = _this.getItems();

      var index = items.findIndex(function (x) {
        return x === focusedItem;
      });

      do {
        index = (index + step + items.length) % items.length;
      } while (!_this.isItemFocusable(items[index]));

      _this.setState({
        focusedItem: items[index]
      });
    };

    _this.canGoBackward = function () {
      return _this.props.activePage > 1;
    };

    _this.canGoForward = function () {
      return _this.props.activePage < _this.props.pagesCount;
    };

    _this.goBackward = function () {
      _this.goToPage(_this.props.activePage - 1);
    };

    _this.goForward = function () {
      _this.goToPage(_this.props.activePage + 1);
    };

    _this.goToPage = function (pageNumber) {
      if (pageNumber >= 1 && pageNumber !== _this.props.activePage && pageNumber <= _this.props.pagesCount) {
        _this.props.onPageChange(pageNumber);
      }
    };

    _this.addGlobalListener = function () {
      var _globalObject$documen;

      if (_this.addedGlobalListener) {
        return;
      }

      (_globalObject$documen = globalObject.document) == null ? void 0 : _globalObject$documen.addEventListener('keydown', _this.handleKeyDown);
      _this.addedGlobalListener = true;
    };

    _this.removeGlobalListener = function () {
      if (_this.addedGlobalListener) {
        var _globalObject$documen2;

        (_globalObject$documen2 = globalObject.document) == null ? void 0 : _globalObject$documen2.removeEventListener('keydown', _this.handleKeyDown);
        _this.addedGlobalListener = false;
      }
    };

    _this.refContainer = function (element) {
      _this.container = element;
    };

    return _this;
  }

  Paging.isForward = function isForward(pageNumber)
  /* %checks */
  {
    return pageNumber === 'forward';
  };

  var _proto = Paging.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var useGlobalListener = this.getProps().useGlobalListener;

    if (useGlobalListener) {
      this.addGlobalListener();
    }
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var useGlobalListener = this.getProps().useGlobalListener;

    if (!prevProps.useGlobalListener && useGlobalListener) {
      this.addGlobalListener();
    }

    if (prevProps.useGlobalListener && !useGlobalListener) {
      this.removeGlobalListener();
    }

    if (prevProps.useGlobalListener !== useGlobalListener) {
      this.setState({
        keyboardControl: useGlobalListener
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.removeGlobalListener();
  };

  _proto.render = function render() {
    var _this2 = this;

    if (this.props.pagesCount < 2 && !this.getProps().shouldBeVisibleWithLessThanTwoPages) {
      return null;
    }

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = theme;
      return _this2.renderMain();
    });
  };

  _proto.renderMain = function renderMain() {
    var _cx4;

    var _this$getProps = this.getProps(),
        dataTid = _this$getProps['data-tid'],
        useGlobalListener = _this$getProps.useGlobalListener;

    return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
      rootNodeRef: this.setRootNode
    }, this.props, getVisualStateDataAttributes({
      disabled: this.props.disabled
    })), /*#__PURE__*/React.createElement("span", {
      tabIndex: this.props.disabled ? -1 : 0,
      "data-tid": dataTid,
      className: cx((_cx4 = {}, _cx4[styles.paging(this.theme)] = true, _cx4[styles.pagingDisabled()] = this.props.disabled, _cx4)),
      onKeyDown: useGlobalListener ? undefined : this.handleKeyDown,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onMouseDown: this.handleMouseDown,
      ref: this.refContainer
    }, this.getItems().map(this.renderItem)));
  };

  return Paging;
}(React.PureComponent), _class2.__KONTUR_REACT_UI__ = 'Paging', _class2.displayName = 'Paging', _class2.defaultProps = {
  component: PagingDefaultComponent,
  shouldBeVisibleWithLessThanTwoPages: true,
  useGlobalListener: false,
  'data-tid': PagingDataTids.root
}, _class2.propTypes = {}, _temp)) || _class) || _class);
Paging.propTypes = {
  /**
   * Current active page
   */
  activePage: number.isRequired,

  /**
   * React component that would be used for rendering items
   *
   * Usefull for router integration
   */
  component: func,

  /**
   * Total page count
   */
  pagesCount: number.isRequired,

  /**
   * Calls when page has been changed
   */
  onPageChange: func.isRequired
};